import React from "react";
import { motion, type Easing } from "framer-motion";
import { Helmet } from "react-helmet-async"; // Assuming react-helmet-async is installed

import {
  FaBullseye,
  FaChartLine,
  FaMoneyBillWave,
  FaThumbsUp,
  FaRocket,
  FaGoogle, // For Google Ads
  FaFacebook, // For Social Media Ads
  FaSearchDollar, // For ROI focus
  FaLightbulb, // For strategy/innovation
  FaHeadset, // For support
  FaQuestionCircle, // For FAQ
  FaUsers, // For team/clients
  FaCogs, // For optimization
  FaSearch, // For keyword research
 
} from "react-icons/fa"; // Added more relevant icons
import { Link } from "react-router-dom";

// Animation variants (reusing from previous files for consistency)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeProcessStep = (index: number) => ({
  hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0, 0, 0.58, 1] as Easing,
    },
  },
});

const PPCServices: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Helmet>
        <title>Best PPC Services in Noida | Google Ads Expert | Nadezhda Venture</title>
        <meta
          name="description"
          content="Maximize your ROI with our top-notch PPC advertising services in Noida. Nadezhda Venture specializes in Google Ads, Facebook Ads, and targeted campaigns that deliver fast, measurable results."
        />
        <meta
          name="keywords"
          content="PPC services Noida, Google Ads agency Noida, pay-per-click, targeted ads, digital marketing, ROI marketing, Facebook Ads, search advertising, Nadezhda Venture"
        />
        <meta name="author" content="Nadezhda Venture" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Best PPC Services in Noida | Nadezhda Venture" />
        <meta property="og:description" content="Drive instant traffic and leads with expert PPC advertising campaigns tailored for your business in Noida." />
        <meta property="og:image" content="https://www.wordstream.com/wp-content/uploads/2021/07/what-is-ppc-1.jpeg" /> {/* PPC related image */}
        <meta property="og:url" content="https://yourdomain.com/ppc-services-noida" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* SECTION 1 - Hero Section */}
      <section className="h-[400px] bg-gradient-to-br from-gray-900 via-black to-gray-950 relative overflow-hidden flex items-center justify-center">
        {/* Background Image */}
        <img
          src="https://media.designrush.com/articles/608361/conversions/12-Best-PPC-platforms-in-2024-(2)-preview.jpg" // PPC specific image
          className="absolute inset-0 w-full h-full object-cover opacity-50 z-0"
          alt="PPC Advertising Background"
        />

        {/* Overlay with blur for glass-like effect */}
        <div className="absolute inset-0 opacity-30 z-0" />

        <motion.div
          className="relative z-10 max-w-4xl px-6 py-32 md:py-48 text-center md:text-left"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInLeft}
        >
          <h1 className="text-3xl md:text-4xl font-extrabold text-orange-500 mb-4">
            Maximize Your ROI with Expert <span className="text-white">PPC Advertising</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
            Drive immediate, targeted traffic to your website and achieve rapid business growth with our advanced Pay-Per-Click strategies in Noida.
          </p>
          <div className="mt-8">
           <Link to="/build-app">
  <button className="bg-orange-700 hover:bg-orange-600 px-6 py-3 rounded-lg font-semibold transition text-white">
    Get Started Now
  </button>
</Link>
          </div>
        </motion.div>
      </section>

      {/* SECTION 2 - Why Choose Our PPC Services? (Re-styled Features Section) */}
      <section className="bg-white text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            The Nadezhda Venture <span className="text-blue-800">PPC Advantage</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaBullseye size={50} className="text-orange-400 mb-4 mx-auto" />,
                title: "Precision Targeting",
                desc: "We pinpoint your ideal customers with hyper-targeted campaigns, ensuring your ads reach the most relevant audience.",
              },
              {
                icon: <FaChartLine size={50} className="text-white mb-4 mx-auto" />,
                title: "Data-Driven Optimization",
                desc: "Leveraging advanced analytics and continuous A/B testing, we refine campaigns for peak performance and maximum ROI.",
              },
              {
                icon: <FaMoneyBillWave size={50} className="text-green-400 mb-4 mx-auto" />,
                title: "Exceptional ROI",
                desc: "Our strategic budget allocation and conversion-focused approach ensure every penny spent delivers tangible returns.",
              },
              {
                icon: <FaRocket size={50} className="text-red-400 mb-4 mx-auto" />,
                title: "Rapid Growth",
                desc: "PPC delivers immediate visibility and traffic, accelerating your business growth and lead generation.",
              },
              {
                icon: <FaThumbsUp size={50} className="text-blue-400 mb-4 mx-auto" />,
                title: "Transparent Reporting",
                desc: "Gain clear insights into your campaign's performance with detailed, easy-to-understand reports and regular communication.",
              },
              {
                icon: <FaHeadset size={50} className="text-purple-400 mb-4 mx-auto" />,
                title: "Dedicated Support",
                desc: "Our team provides personalized attention and expert guidance throughout your PPC journey.",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-orange-600"
              >
                {card.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-600">{card.title}</h3>
                <p className="text-gray-300 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 - Our Comprehensive PPC Services */}
      <section className="bg-blue-950 text-white py-20 px-6 relative overflow-hidden">
        {/* Subtle background graphic */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')] opacity-10 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-gray-950 to-transparent opacity-50"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Our Core <span className="text-orange-500">PPC Service Offerings</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaGoogle size={30} className="text-orange-500" />,
                title: "Google Ads Management",
                description: "Expert setup and optimization of Search, Display, Shopping, and Video campaigns on the world's largest ad platform.",
              },
              {
                icon: <FaFacebook size={30} className="text-orange-500" />,
                title: "Social Media Advertising",
                description: "Targeted ad campaigns on Facebook, Instagram, LinkedIn, and other platforms to reach your audience where they spend their time.",
              },
              {
                icon: <FaSearch size={30} className="text-orange-500" />,
                title: "Keyword Research & Strategy",
                description: "In-depth keyword analysis to identify high-converting terms and phrases for your campaigns.",
              },
              {
                icon: <FaCogs size={30} className="text-orange-500" />,
                title: "Campaign Optimization & A/B Testing",
                description: "Continuous monitoring, refinement, and testing of ad creatives, landing pages, and bidding strategies for optimal performance.",
              },
              {
                icon: <FaChartLine size={30} className="text-orange-500" />,
                title: "Performance Reporting & Analytics",
                description: "Transparent, actionable reports on your campaign's performance, ROI, and key metrics.",
              },
              {
                icon: <FaSearchDollar size={30} className="text-orange-500" />,
                title: "Conversion Rate Optimization (CRO)",
                description: "Improving your website and landing pages to convert more visitors into leads or customers.",
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300 border border-gray-700 hover:border-orange-500"
              >
                <div className="flex-shrink-0">{service.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-orange-600">{service.title}</h3>
                  <p className="text-gray-800 text-base">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-white text-white py-24 px-6 overflow-hidden">
  <div className="absolute -top-32 left-0 w-[140%] h-72 rotate-3 bg-orange-500/5 blur-2xl pointer-events-none"></div>

  <div className="max-w-7xl mx-auto relative z-10">
    <h2 className="text-5xl font-extrabold text-center text-orange-500 mb-6 tracking-tight">
      Our PPC Magic Formula
    </h2>
    <p className="text-center text-black text-lg mb-20 max-w-2xl mx-auto">
      A visually rich, conversion-focused process crafted by experts to get results.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
      {[
        {
          title: "1. Market Discovery",
          desc: "We understand your business DNA and analyze what makes your audience click.",
          icon: "🔍",
        },
        {
          title: "2. Smart Ad Creation",
          desc: "We build compelling ad copies, creatives, and landing pages that convert.",
          icon: "🎨",
        },
        {
          title: "3. Launch & Analyze",
          desc: "We launch fast, measure everything, and adapt in real time.",
          icon: "🚀",
        },
        {
          title: "4. Report & Refine",
          desc: "Clear, visual reports and constant optimization to maximize ROI.",
          icon: "📊",
        },
      ].map((step, i) => (
        <motion.div
          key={i}
          className="relative group rounded-2xl bg-blue-950 p-8 shadow-2xl border border-orange-500/10 hover:border-orange-500/50 transition-all duration-300 hover:scale-[1.03]"
          initial={{ opacity: 0, y: i % 2 === 0 ? 50 : -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: i * 0.2 }}
          viewport={{ once: true }}
        >
          <div className="text-4xl mb-4">{step.icon}</div>
          <h3 className="text-2xl font-bold text-orange-400 mb-2">{step.title}</h3>
          <p className="text-gray-300 text-md">{step.desc}</p>

          {/* Glow Border Effect */}
          <div className="absolute -inset-[2px] rounded-2xl z-[-1] opacity-0 group-hover:opacity-100 transition duration-500 blur-lg bg-gradient-to-br from-orange-500/20 to-orange-400/30"></div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* SECTION 5 - Why Choose Nadezhda Venture for PPC */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-circles.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            Why Partner with Us for Your <span className="text-orange-500">PPC Success</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <FaUsers size={50} className="text-orange-400 mb-4 mx-auto" />,
                title: "Certified PPC Experts",
                description: "Our team consists of Google Ads certified professionals with deep expertise in various ad platforms.",
              },
              {
                icon: <FaLightbulb size={50} className="text-white mb-4 mx-auto" />,
                title: "Innovation & Adaptability",
                description: "We stay ahead of evolving ad trends and algorithm changes, ensuring your campaigns are always optimized.",
              },
              {
                icon: <FaSearchDollar size={50} className="text-orange-400 mb-4 mx-auto" />,
                title: "Focus on ROI",
                description: "Our primary goal is to deliver measurable returns on your ad spend, focusing on conversions and profitability.",
              },
            ].map((reason, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-700"
              >
                {reason.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-500">{reason.title}</h3>
                <p className="text-white leading-relaxed">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 - Client Testimonials (Re-styled existing section) */}
      {/* <section className="bg-gray-800 text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            What Our Clients Say About Our <span className="text-orange-500">PPC Expertise</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                quote: "Nadezhda Venture's PPC services are outstanding! Our ad spend became incredibly efficient, and we saw a direct increase in high-quality leads and sales.",
                author: "Alice W.",
                company: "E-commerce Founder",
              },
              {
                quote: "The team's transparency and data-driven approach to PPC are truly commendable. We finally found an agency that delivers consistent ROI and clear communication.",
                author: "David K.",
                company: "Marketing Director",
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="bg-gray-900/70 backdrop-blur-sm p-8 rounded-lg shadow-xl text-left border-l-4 border-orange-500 hover:shadow-2xl transition-shadow duration-300"
              >
                <p className="text-lg italic mb-6 text-gray-100">"{testimonial.quote}"</p>
                <p className="font-semibold text-orange-400">{testimonial.author}</p>
                <p className="text-gray-300 text-sm">{testimonial.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* SECTION 7 - Frequently Asked Questions (FAQ) */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dot-grid.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Common Questions About <span className="text-orange-500">PPC Advertising</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                question: "What is PPC and how can it benefit my business?",
                answer: "PPC (Pay-Per-Click) is an online advertising model where advertisers pay a fee each time one of their ads is clicked. It benefits your business by providing immediate traffic, precise audience targeting, and measurable ROI, making it highly effective for lead generation and sales.",
              },
              {
                question: "Which platforms do you manage PPC campaigns on?",
                answer: "We primarily manage campaigns on Google Ads (Search, Display, Shopping, Video, App campaigns) and major social media platforms like Facebook, Instagram, and LinkedIn. We choose the best platforms based on your target audience and business objectives.",
              },
              {
                question: "How long does it take to see results from PPC?",
                answer: "One of the major advantages of PPC is its speed. You can start seeing traffic and conversions almost immediately after campaigns launch, often within days. However, optimal performance and ROI typically improve over the first few weeks and months with continuous optimization.",
              },
              {
                question: "What is your pricing model for PPC services?",
                answer: "Our pricing is flexible and typically based on a combination of a management fee (either a percentage of ad spend or a flat monthly fee) and the actual ad spend budget you allocate. We offer tailored plans to suit various business sizes and goals, focusing on transparency and value.",
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-gray-800/70 backdrop-blur-sm p-6 rounded-lg shadow-md border border-gray-700"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer text-lg font-semibold text-white">
                    {faq.question}
                    <FaQuestionCircle className="text-orange-400 group-open:rotate-180 transition-transform duration-300" />
                  </summary>
                  <p className="mt-4 text-gray-300 leading-relaxed">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


     
    </div>
  );
};

export default PPCServices;